import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	FormControlLabel,
	Grid,
	IconButton,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Link,
	useTheme,
} from "@mui/material";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from "recharts";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	ApiRequestFailed,
	FreeEmailVerificationSettings,
	getFreeEmailVerificationSettings,
	getFreeEmailVerificationStats,
	retryFn,
	updateFreeEmailVerificationSettings,
} from "@lib/apis";
import { useSnackbar } from "notistack";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { Add, Delete, OpenInNew, Save } from "@mui/icons-material";
import { format } from "date-fns";

interface FreeVerificationRequest {
	task_uid: string;
	ip_address: string | null;
	origin: string;
	email_count: number;
	verified_count: number;
	created_at: string;
	results_url: string;
}

interface FreeVerificationDaily {
	date: string;
	calls: number;
	emails_submitted: number;
	emails_verified: number;
}

interface FreeVerificationTotals {
	total_calls: number;
	total_emails_submitted: number;
	total_emails_verified: number;
}

interface FreeVerificationStats {
	days: number;
	window: FreeVerificationTotals;
	lifetime: FreeVerificationTotals;
	daily: FreeVerificationDaily[];
	recent_requests: FreeVerificationRequest[];
}

const STATS_WINDOW_DAYS = 30;

export default function AdminFreeEmailVerification() {
	const theme = useTheme();
	const { enqueueSnackbar } = useSnackbar();
	const [isEnabled, setIsEnabled] = useState(true);
	const [requestsPerMinute, setRequestsPerMinute] = useState("5");
	const [maxEmailsPerRequest, setMaxEmailsPerRequest] = useState("100");
	const [restrictToAllowedOrigins, setRestrictToAllowedOrigins] = useState(true);
	const [allowedOrigins, setAllowedOrigins] = useState<string[]>([]);
	const [newOrigin, setNewOrigin] = useState("");
	const [pageData, setPageData] = useState<FreeEmailVerificationSettings | null>(null);

	const pageDataQuery = useQuery({
		queryKey: ["adminFreeEmailVerificationSettings"],
		queryFn: () => getFreeEmailVerificationSettings(),
		refetchOnWindowFocus: false,
		retry: retryFn,
		gcTime: 0,
	});

	const statsQuery = useQuery({
		queryKey: ["adminFreeEmailVerificationStats"],
		queryFn: () => getFreeEmailVerificationStats(STATS_WINDOW_DAYS),
		refetchOnWindowFocus: false,
		retry: retryFn,
		gcTime: 0,
	});

	useEffect(() => {
		if (pageDataQuery.data) {
			const data = pageDataQuery.data.data as FreeEmailVerificationSettings;
			setPageData(data);
			setIsEnabled(data.is_enabled);
			setRequestsPerMinute(String(data.requests_per_minute_per_ip));
			setMaxEmailsPerRequest(String(data.max_emails_per_request));
			setRestrictToAllowedOrigins(data.restrict_to_allowed_origins);
			setAllowedOrigins(data.allowed_origins || []);
		}
	}, [pageDataQuery.data]);

	const saveSettingsMutation = useMutation({
		mutationFn: () =>
			updateFreeEmailVerificationSettings({
				is_enabled: isEnabled,
				requests_per_minute_per_ip: Number(requestsPerMinute),
				max_emails_per_request: Number(maxEmailsPerRequest),
				restrict_to_allowed_origins: restrictToAllowedOrigins,
				allowed_origins: allowedOrigins,
			}),
		onSuccess: (response) => {
			const data = response.data.data as FreeEmailVerificationSettings;
			setPageData(data);
			setAllowedOrigins(data.allowed_origins || []);
			enqueueSnackbar("Free email verification settings saved", { variant: "success" });
			pageDataQuery.refetch();
		},
		onError: (error: ApiRequestFailed) => {
			enqueueSnackbar(error.data?.message || "Failed to save settings", { variant: "error" });
		},
		retry: retryFn,
	});

	const handleAddOrigin = () => {
		const origin = newOrigin.trim().replace(/\/+$/, "");
		if (!origin) {
			return;
		}
		if (allowedOrigins.includes(origin)) {
			enqueueSnackbar("That origin is already in the list", { variant: "warning" });
			return;
		}
		setAllowedOrigins([...allowedOrigins, origin]);
		setNewOrigin("");
	};

	const handleRemoveOrigin = (origin: string) => {
		setAllowedOrigins(allowedOrigins.filter((entry) => entry !== origin));
	};

	const handleSave = () => {
		if (Number(requestsPerMinute) < 1 || Number(maxEmailsPerRequest) < 1) {
			enqueueSnackbar("Rate limit and batch size must both be at least 1", { variant: "error" });
			return;
		}
		saveSettingsMutation.mutate();
	};

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (pageDataQuery.isLoading) {
		return <PageLoading />;
	}

	if (pageDataQuery.error) {
		return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />;
	}

	const stats = statsQuery.data?.data as FreeVerificationStats | undefined;
	const daily = stats?.daily ?? [];
	const hasTraffic = daily.some((d) => d.calls > 0);

	// MUI's dark defaults sit too light and too desaturated to hold up as chart marks,
	// so dark mode gets its own steps off the same ramps rather than a flipped palette.
	const isDark = theme.palette.mode === "dark";
	const seriesColor = isDark
		? { submitted: "#2196f3", verified: "#43a047" }
		: { submitted: "#1976d2", verified: "#2e7d32" };

	const axisProps = {
		stroke: theme.palette.divider,
		tick: { fill: theme.palette.text.secondary, fontSize: 11 },
		tickLine: false,
	};
	const tooltipProps = {
		contentStyle: {
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.divider}`,
			borderRadius: 8,
			boxShadow: theme.shadows[3],
		},
		labelStyle: { color: theme.palette.text.primary, fontWeight: 600 },
		itemStyle: { color: theme.palette.text.secondary },
		cursor: { fill: theme.palette.action.hover },
		labelFormatter: (label: React.ReactNode) =>
			typeof label === "string" ? format(new Date(label), "PP") : label,
	};
	const dayTick = (value: string) => format(new Date(value), "MMM d");
	// ~6 labels regardless of window length, so ticks never collide.
	const tickInterval = Math.max(0, Math.ceil(daily.length / 6) - 1);

	return (
		<Box>
			<Typography variant="h4" gutterBottom>
				Free Email Verification
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Configuration and usage for the free, unauthenticated email verification API used by the
				marketing site. Verification is quick only — greylisted addresses are reported as
				unverifiable instead of being retried.
			</Typography>

			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Stack spacing={3}>
						{pageData && (
							<Box>
								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									Current Status
								</Typography>
								<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
									<Chip
										label={pageData.is_enabled ? "ENABLED" : "DISABLED"}
										color={pageData.is_enabled ? "success" : "error"}
										size="small"
									/>
									{pageData.updated_by_email && (
										<Typography variant="caption" color="text.secondary">
											last changed by {pageData.updated_by_email}
										</Typography>
									)}
								</Stack>
								{pageData.last_updated && (
									<Typography variant="caption" color="text.secondary" display="block">
										Last updated: {format(new Date(pageData.last_updated), "PPpp")}
									</Typography>
								)}
							</Box>
						)}

						<Divider />

						<FormControlLabel
							control={
								<Switch
									checked={isEnabled}
									onChange={(e) => setIsEnabled(e.target.checked)}
									color="primary"
								/>
							}
							label={
								<Box>
									<Typography variant="body1">
										{isEnabled ? "API Enabled" : "API Disabled"}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{isEnabled
											? "The free verification API is accepting requests"
											: "The API returns 503 to every caller"}
									</Typography>
								</Box>
							}
						/>

						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									type="number"
									label="Rate limit (requests per minute per IP)"
									value={requestsPerMinute}
									onChange={(e) => setRequestsPerMinute(e.target.value)}
									inputProps={{ min: 1 }}
									helperText="Requests above this in any 60 second window get a 429"
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									type="number"
									label="Max emails per request"
									value={maxEmailsPerRequest}
									onChange={(e) => setMaxEmailsPerRequest(e.target.value)}
									inputProps={{ min: 1 }}
									helperText="Batch size cap for a single call"
								/>
							</Grid>
						</Grid>

						<Divider />

						<FormControlLabel
							control={
								<Switch
									checked={restrictToAllowedOrigins}
									onChange={(e) => setRestrictToAllowedOrigins(e.target.checked)}
									color="primary"
								/>
							}
							label={
								<Box>
									<Typography variant="body1">Restrict to our domains only</Typography>
									<Typography variant="caption" color="text.secondary">
										{restrictToAllowedOrigins
											? "Only requests from the origins below are accepted"
											: "Requests from any origin are accepted"}
									</Typography>
								</Box>
							}
						/>

						<Box>
							<Typography variant="subtitle2" gutterBottom>
								Allowed Origins
							</Typography>
							<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
								Enter the full origin including the scheme, e.g. https://deliveryman.ai — a bare
								domain will not match. This list also drives the CORS headers, so a domain added
								here works in the browser without a redeploy.
							</Typography>
							<Stack direction="row" spacing={1} sx={{ mb: 2 }}>
								<TextField
									fullWidth
									size="small"
									placeholder="https://deliveryman.ai"
									value={newOrigin}
									onChange={(e) => setNewOrigin(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault();
											handleAddOrigin();
										}
									}}
									disabled={!restrictToAllowedOrigins}
								/>
								<Button
									variant="outlined"
									startIcon={<Add />}
									onClick={handleAddOrigin}
									disabled={!restrictToAllowedOrigins}
								>
									Add
								</Button>
							</Stack>
							{allowedOrigins.length === 0 ? (
								<Typography variant="caption" color="text.secondary">
									No origins added. With the restriction on, every request will be rejected.
								</Typography>
							) : (
								<Stack spacing={1}>
									{allowedOrigins.map((origin) => (
										<Stack
											key={origin}
											direction="row"
											alignItems="center"
											justifyContent="space-between"
										>
											<Typography variant="body2">{origin}</Typography>
											<IconButton
												size="small"
												onClick={() => handleRemoveOrigin(origin)}
												disabled={!restrictToAllowedOrigins}
											>
												<Delete fontSize="small" />
											</IconButton>
										</Stack>
									))}
								</Stack>
							)}
						</Box>

						<Box display="flex" justifyContent="flex-end">
							<Button
								variant="contained"
								startIcon={<Save />}
								onClick={handleSave}
								disabled={saveSettingsMutation.isPending}
							>
								{saveSettingsMutation.isPending ? "Saving..." : "Save Settings"}
							</Button>
						</Box>
					</Stack>
				</CardContent>
			</Card>

			<Card>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Usage
					</Typography>

					{statsQuery.isLoading ? (
						<Typography variant="body2" color="text.secondary">
							Loading stats...
						</Typography>
					) : (
						<>
							<Grid container spacing={2} sx={{ mb: 3 }}>
								<Grid item xs={12} sm={4}>
									<Typography variant="caption" color="text.secondary">
										API calls (last {stats?.days ?? STATS_WINDOW_DAYS} days)
									</Typography>
									<Typography variant="h5">{stats?.window.total_calls ?? 0}</Typography>
									<Typography variant="caption" color="text.secondary">
										{stats?.lifetime.total_calls ?? 0} all time
									</Typography>
								</Grid>
								<Grid item xs={12} sm={4}>
									<Typography variant="caption" color="text.secondary">
										Emails submitted
									</Typography>
									<Typography variant="h5">
										{stats?.window.total_emails_submitted ?? 0}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{stats?.lifetime.total_emails_submitted ?? 0} all time
									</Typography>
								</Grid>
								<Grid item xs={12} sm={4}>
									<Typography variant="caption" color="text.secondary">
										Emails verified
									</Typography>
									<Typography variant="h5">
										{stats?.window.total_emails_verified ?? 0}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{stats?.lifetime.total_emails_verified ?? 0} all time
									</Typography>
								</Grid>
							</Grid>

							<Typography variant="subtitle2" gutterBottom>
								Daily Traffic
							</Typography>
							{!hasTraffic ? (
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									No traffic in the last {stats?.days ?? STATS_WINDOW_DAYS} days.
								</Typography>
							) : (
								<Grid container spacing={3} sx={{ mb: 3 }}>
									<Grid item xs={12} md={6}>
										<Typography variant="caption" color="text.secondary">
											API calls per day
										</Typography>
										<Box sx={{ height: 200, mt: 1 }}>
											<ResponsiveContainer width="100%" height="100%">
												<BarChart data={daily} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
													<CartesianGrid
														strokeDasharray="3 3"
														vertical={false}
														stroke={theme.palette.divider}
													/>
													<XAxis
														dataKey="date"
														tickFormatter={dayTick}
														interval={tickInterval}
														{...axisProps}
													/>
													<YAxis allowDecimals={false} width={44} {...axisProps} />
													<RechartsTooltip {...tooltipProps} />
													<Bar
														dataKey="calls"
														name="API calls"
														fill={seriesColor.submitted}
														radius={[4, 4, 0, 0]}
													/>
												</BarChart>
											</ResponsiveContainer>
										</Box>
									</Grid>
									<Grid item xs={12} md={6}>
										<Typography variant="caption" color="text.secondary">
											Emails per day
										</Typography>
										<Box sx={{ height: 200, mt: 1 }}>
											<ResponsiveContainer width="100%" height="100%">
												<BarChart data={daily} barGap={2} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
													<CartesianGrid
														strokeDasharray="3 3"
														vertical={false}
														stroke={theme.palette.divider}
													/>
													<XAxis
														dataKey="date"
														tickFormatter={dayTick}
														interval={tickInterval}
														{...axisProps}
													/>
													<YAxis allowDecimals={false} width={44} {...axisProps} />
													<RechartsTooltip {...tooltipProps} />
													<Legend
														wrapperStyle={{ fontSize: 12, color: theme.palette.text.secondary }}
													/>
													<Bar
														dataKey="emails_submitted"
														name="Submitted"
														fill={seriesColor.submitted}
														radius={[4, 4, 0, 0]}
													/>
													<Bar
														dataKey="emails_verified"
														name="Verified"
														fill={seriesColor.verified}
														radius={[4, 4, 0, 0]}
													/>
												</BarChart>
											</ResponsiveContainer>
										</Box>
									</Grid>
								</Grid>
							)}

							<Typography variant="subtitle2" gutterBottom>
								Recent Requests
							</Typography>
							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Time</TableCell>
											<TableCell>IP</TableCell>
											<TableCell>Origin</TableCell>
											<TableCell align="right">Submitted</TableCell>
											<TableCell align="right">Verified</TableCell>
											<TableCell>Results</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{(stats?.recent_requests ?? []).length === 0 ? (
											<TableRow>
												<TableCell colSpan={6}>
													<Typography variant="body2" color="text.secondary">
														No requests yet.
													</Typography>
												</TableCell>
											</TableRow>
										) : (
											stats?.recent_requests.map((item) => (
												<TableRow key={item.task_uid}>
													<TableCell>
														{format(new Date(item.created_at), "PPp")}
													</TableCell>
													<TableCell>{item.ip_address || "-"}</TableCell>
													<TableCell>{item.origin || "-"}</TableCell>
													<TableCell align="right">{item.email_count}</TableCell>
													<TableCell align="right">{item.verified_count}</TableCell>
													<TableCell>
														<Link
															href={item.results_url}
															target="_blank"
															rel="noopener noreferrer"
															underline="hover"
															sx={{
																display: "inline-flex",
																alignItems: "center",
																gap: 0.5,
																fontFamily: "monospace",
																fontSize: 12,
															}}
														>
															{item.task_uid.slice(0, 8)}
															<OpenInNew sx={{ fontSize: 13 }} />
														</Link>
													</TableCell>
												</TableRow>
											))
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</>
					)}
				</CardContent>
			</Card>
		</Box>
	);
}
